import Cookies from "js-cookie";
import React, { useState, useRef, useMemo } from "react";

const loadPreviousAuth = () => {
    const access = localStorage.getItem("access");
    const refresh = localStorage.getItem("refresh");
    if (access && refresh) {
        return { access, refresh };
    }
    return null;
};

const saveAuth = (auth) => {
    if(auth) {
        localStorage.setItem("access", auth.access);
        localStorage.setItem("refresh", auth.refresh);
    } else {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
    }
};

const previousAuth = loadPreviousAuth();

const addAPIHeaders = headers => {
    return {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "x-csrftoken": Cookies.get("csrftoken"),
        ...headers,
    }
};

export const useAuth = () => {
    const [ auth, setAuth ] = useState(previousAuth); // TODO change to useLocalStorage ?
    const authRef = useRef(previousAuth);
    const refreshPromise = useRef(null);

    const storeAuth = (auth) => {
        authRef.current = auth;
        setAuth(auth);
        saveAuth(auth);
    };

    const login = async (username, password) => {
        const response = await fetch(
            `${process.env.REACT_APP_API_ROOT}auth/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username, password })
            }
        )
        if (response.ok) {
            const data = await response.json();
            storeAuth(data);
            return data;
        } else if(response.status === 401) {
            throw new LoginFailedError();
        } else {
            throw new Error("Unknown error");
        }
    };

    const signup = async (username, password) => {
        const response = await fetch(
            `${process.env.REACT_APP_API_ROOT}auth/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username, password })
            }
        );
        const data = await response.json();
        if(response.ok) {
            storeAuth(data);
            return data;
        } else {
            throw new SignupError(data);
        }
    };

    const logout = async () => {
        storeAuth(null);
    };

    const refresh = async () => {
        const response = await fetch(
            `${process.env.REACT_APP_API_ROOT}auth/token/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refresh: authRef.current.refresh })
            }
        );
        if (response.ok) {
            storeAuth(await response.json());
        } else {
            storeAuth(null);
        }
        refreshPromise.current = null;
    }

    const chainedRefresh = async () => {
        if(!refreshPromise.current) {
            refreshPromise.current = refresh();
        }
        await refreshPromise.current;
    };

    const authOptions = options => {
        return {
            ...options,
            headers: addAPIHeaders({
                ...options.headers,
                Authorization: `Bearer ${authRef.current.access}`
            })
        };
    };

    const userScopedFetch = useMemo(() => {
        if(!auth) {
            return null;
        }
        const fetchWithAuth = async (resource, options) => {
            if(!authRef.current) {
                return;
            }
            let response = await fetch(resource, authOptions(options));
            if(response.status === 401) {
                await chainedRefresh();
                if(!authRef.current) {
                    return;
                };
                response = await fetch(resource, authOptions(options));
            }
            return response;
        };
        // So it can be used as a key in a ReactQuery
        fetchWithAuth.ident = auth?.access;
        return fetchWithAuth;
    }, [auth]);

    return {
        login, logout, signup, userScopedFetch
    };
}

export const applyCode = async (code, state, spaceScopedFetch) => {
    const response = await spaceScopedFetch(
        `${process.env.REACT_APP_API_ROOT}auth/code`, {
            method: 'POST',
            body: JSON.stringify({
                code,
                ...state
            })
        }
    );
    if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return {};
}

export class SignupError extends Error {
    constructor(error) {
        super("SignupError");
        this.error = error;
    }
}

export class LoginFailedError extends Error {
}
